import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from '@/hooks/useDebounce';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import {
  useCreateAIBrandProfile,
  useBrandProfileDraft,
  useSaveBrandProfileDraft,
  useDeleteBrandProfileDraft,
} from '@/hooks/useAIBrandProfiles';
import type { AIBrandProfile, AIBrandProfileFormData } from '@/types/ai';
import { DEFAULT_PROFILE_FORM_DATA } from '@/types/ai';
import { WizardModal } from '@/components/ui/WizardModal';
import {
  WIZARD_STEPS,
  useBrandProfileWizardSteps,
  ProfileWizardContent,
  ProfileWizardFooter,
  mapFormDataToInput,
} from '@/components/settings/AIBrandProfileSettings';
import { isGlobalGatingHandledError } from '@/services/api';

/**
 * AIBrandProfileCreateModal
 *
 * Shared modal for creating an AI Brand Profile, used by both the Settings page
 * and the Dashboard AIProfilesSection widget.
 *
 * Draft persistence is SERVER-SIDE (resume across devices) via
 * GET/PUT/DELETE /api/ai/brand-profiles/draft:
 * - Autosave (2 s debounce) to the server while the modal is open, with an inline
 *   "Saving…" / "Draft saved" indicator in the footer.
 * - Explicit "Save draft & close" button — an obvious, intentional persist-and-leave action.
 * - Restore the draft (and jump to its saved step) whenever the modal is (re)opened.
 * - Discard draft button (step 0 only) with confirmation, calls DELETE.
 * - The server deletes the draft automatically on successful profile creation;
 *   we invalidate/reset the local draft cache to match (see useCreateAIBrandProfile).
 */

interface Notification {
  type: 'success' | 'error';
  message: string;
}

interface AIBrandProfileCreateModalProps {
  /** Controlled open state */
  open: boolean;
  /** Called when the modal wants to open or close itself */
  onOpenChange: (open: boolean) => void;
  /** Called after a successful create mutation (receives the new profile) */
  onSuccess?: (profile: AIBrandProfile) => void;
  /** Called to surface a notification toast in the parent */
  onNotify?: (notification: Notification) => void;
}

type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Serialize the pieces of state that make up the persisted draft, for change detection. */
const serializeDraft = (draftData: AIBrandProfileFormData, step: number): string =>
  JSON.stringify({ draftData, step });

export const AIBrandProfileCreateModal = ({
  open,
  onOpenChange,
  onSuccess,
  onNotify,
}: AIBrandProfileCreateModalProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { steps: wizardSteps, descriptions: stepDescriptions } = useBrandProfileWizardSteps();
  const [formData, setFormData] = useState<AIBrandProfileFormData>(DEFAULT_PROFILE_FORM_DATA);
  const [step, setStep] = useState(0);
  const [draftSaveStatus, setDraftSaveStatus] = useState<DraftSaveStatus>('idle');

  const { hasTeam } = useEffectiveTeamId();
  const createMutation = useCreateAIBrandProfile();
  const { data: draft, isLoading: isDraftLoading } = useBrandProfileDraft();
  const saveDraftMutation = useSaveBrandProfileDraft();
  const deleteDraftMutation = useDeleteBrandProfileDraft();

  // Last {draftData, step} persisted to the server — lets us skip redundant PUTs
  // and avoid autosaving before we've decided what to initialize with.
  const lastSavedRef = useRef<string | null>(null);
  const hasInitializedRef = useRef(false);

  const debouncedFormData = useDebounce(formData, 2000);
  const debouncedStep = useDebounce(step, 2000);

  // Dirty whenever the user has entered anything beyond a blank wizard, even
  // if the 2s autosave debounce hasn't flushed to the server draft yet.
  const isDirty = open && serializeDraft(formData, step) !== serializeDraft(DEFAULT_PROFILE_FORM_DATA, 0);

  // Register this editor's dirty state globally while it is open, so the
  // auto-reload mechanism defers to a manual refresh banner instead of
  // silently discarding in-progress (not-yet-autosaved) edits.
  useUnsavedGuard(isDirty, 'ai-brand-profile-create');

  // When the modal opens, restore the server draft (if any) once it has loaded,
  // and jump to its saved step. If there is no draft, start fresh.
  useEffect(() => {
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }
    if (hasInitializedRef.current || isDraftLoading) return;

    hasInitializedRef.current = true;

    if (draft) {
      // Merge with defaults so an older draft that predates a newer wizard field
      // doesn't leave that field `undefined` and crash a later step render.
      const mergedFormData: AIBrandProfileFormData = {
        ...DEFAULT_PROFILE_FORM_DATA,
        ...draft.draftData,
      };
      // Clamp the restored step so a tampered/out-of-range value can't land on a
      // blank wizard body.
      const clampedStep = Math.min(Math.max(draft.step ?? 0, 0), WIZARD_STEPS.length - 1);
      setFormData(mergedFormData);
      setStep(clampedStep);
      lastSavedRef.current = serializeDraft(mergedFormData, clampedStep);
      onNotify?.({ type: 'success', message: t('settings:settings_ai_profile_draft_restored') });
    } else {
      setFormData(DEFAULT_PROFILE_FORM_DATA);
      setStep(0);
      lastSavedRef.current = serializeDraft(DEFAULT_PROFILE_FORM_DATA, 0);
    }
    // We only want this to run once per open→loaded transition; `draft`, `onNotify`,
    // and `t` are intentionally omitted so this doesn't re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isDraftLoading]);

  // Reset the "Draft saved" indicator back to idle a few seconds after success.
  useEffect(() => {
    if (draftSaveStatus !== 'saved') return;
    const timer = setTimeout(() => setDraftSaveStatus('idle'), 3000);
    return () => clearTimeout(timer);
  }, [draftSaveStatus]);

  // Autosave — debounced PUT of formData/step to the server while the modal is open.
  useEffect(() => {
    if (!open || !hasInitializedRef.current || !hasTeam) return;

    const serialized = serializeDraft(debouncedFormData, debouncedStep);
    if (serialized === lastSavedRef.current) return;

    lastSavedRef.current = serialized;
    setDraftSaveStatus('saving');
    saveDraftMutation.mutate(
      { draftData: debouncedFormData, step: debouncedStep },
      {
        onSuccess: () => setDraftSaveStatus('saved'),
        onError: () => setDraftSaveStatus('error'),
      }
    );
    // Only the debounced values (plus open/hasTeam) should trigger a save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormData, debouncedStep, open, hasTeam]);

  const handleClose = () => {
    // Best-effort final save so edits within the debounce window aren't lost.
    // Silent (no toast) — "Save draft & close" below is the explicit, visible action.
    if (hasTeam) {
      const serialized = serializeDraft(formData, step);
      if (serialized !== lastSavedRef.current) {
        lastSavedRef.current = serialized;
        saveDraftMutation.mutate({ draftData: formData, step });
      }
    }
    onOpenChange(false);
  };

  const handleSaveDraftAndClose = () => {
    lastSavedRef.current = serializeDraft(formData, step);
    setDraftSaveStatus('saving');
    saveDraftMutation.mutate(
      { draftData: formData, step },
      {
        onSuccess: () => {
          setDraftSaveStatus('saved');
          onNotify?.({ type: 'success', message: t('settings:settings_ai_profile_draft_saved_notify') });
        },
        onError: (error) => {
          setDraftSaveStatus('error');
          if (isGlobalGatingHandledError(error)) return;
          onNotify?.({ type: 'error', message: t('settings:settings_ai_profile_draft_save_error') });
        },
      }
    );
    onOpenChange(false);
  };

  const handleDiscard = () => {
    deleteDraftMutation.mutate(undefined, {
      onSuccess: () => {
        lastSavedRef.current = null;
        setFormData(DEFAULT_PROFILE_FORM_DATA);
        setStep(0);
        setDraftSaveStatus('idle');
        onOpenChange(false);
      },
    });
  };

  const handleSubmit = (status: 'DRAFT' | 'ACTIVE' = 'ACTIVE') => {
    if (!formData.name.trim()) {
      onNotify?.({ type: 'error', message: 'Brand World name is required' });
      return;
    }

    const input = { ...mapFormDataToInput(formData), status };

    createMutation.mutate(input, {
      onSuccess: (profile) => {
        // The backend deletes the server-side draft when a profile is created;
        // useCreateAIBrandProfile already invalidates the cached draft query.
        lastSavedRef.current = null;
        onOpenChange(false);
        setFormData(DEFAULT_PROFILE_FORM_DATA);
        setStep(0);
        setDraftSaveStatus('idle');
        onSuccess?.(profile);
      },
    });
  };

  return (
    <WizardModal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        } else {
          onOpenChange(true);
        }
      }}
      preventClose={true}
      title={t('settings:settings_ai_wizard_title_create')}
      size="xl"
      steps={wizardSteps}
      currentStep={step}
      onStepClick={(index) => index < step && setStep(index)}
      stepDescription={stepDescriptions[step]}
      footer={
        <ProfileWizardFooter
          formData={formData}
          onChange={setFormData}
          step={step}
          onStepChange={setStep}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          onDiscard={handleDiscard}
          hasDraft={!!draft}
          onSaveDraftAndClose={handleSaveDraftAndClose}
          draftSaveStatus={draftSaveStatus}
          isSubmitting={createMutation.isPending}
          mode="create"
        />
      }
    >
      <ProfileWizardContent formData={formData} onChange={setFormData} step={step} />
    </WizardModal>
  );
};
